import { Bar } from "react-chartjs-2";
import ChartCard from "./ChartsCard";
import { groupedBarOptions } from "./ChartOptions/barChartOptions";
import { useProductCounter } from "../../hooks/useProductCounter";
import { useState, useEffect } from "react";

export default function ProductServiceBarChart() {
  const { years, getProductCountsForYear, isLoading, PRODUCT_NAMES } = useProductCounter();
  const [selectedYear, setSelectedYear] = useState("");
  const [productData, setProductData] = useState<any>([]);

  // Custom options for product chart
  const productChartOptions = {
    ...groupedBarOptions,
    plugins: {
      ...groupedBarOptions?.plugins,
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: () => '',
          label: (context: any) => {
            const productLabels = ["ProLab GB-2", "ProLab GB-3", "ProLab GB-4", "ProLab GB Custom"];
            return `${productLabels[context.dataIndex]}: ${context.parsed.y}`;
          }
        },
        displayColors: false,
      },
    },
    scales: {
      ...groupedBarOptions?.scales,
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          color: "rgba(0, 0, 0, 0.6)",
          precision: 0, // Only show whole numbers
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "rgba(0, 0, 0, 0.6)",
        },
      },
    },
    maintainAspectRatio: false,
    responsive: true,
    onHover: (event: any, chartElements: any) => {
      const canvas = event.native?.target;
      if (canvas) {
        if (chartElements && chartElements.length > 0) {
          canvas.style.cursor = 'pointer';
        } else {
          canvas.style.cursor = 'default';
        }
      }
    },
  };

  // Set default year on load
  useEffect(() => {
    if (years.length > 0 && !selectedYear) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  // Fetch product data when year changes
  useEffect(() => {
    const fetchProductData = async () => {
      if (selectedYear) {
        try {
          const data = await getProductCountsForYear(selectedYear);
          setProductData(data);
        } catch (error) {
          console.error("Error fetching product data:", error);
          setProductData(new Array(PRODUCT_NAMES.length).fill(0));
        }
      }
    };

    fetchProductData();
  }, [selectedYear, getProductCountsForYear, PRODUCT_NAMES.length]);

  // Chart data configuration
  const productServiceVisitsData = {
    labels: ["ProLab GB-2", "ProLab GB-3", "ProLab GB-4", "ProLab GB Custom"],
    datasets: [
      {
        label: "",
        data: productData,
        backgroundColor: [
          "rgba(220, 38, 38, 0.8)",    // Red for GB-2
          "rgba(37, 99, 235, 0.8)",    // Blue for GB-3
          "rgba(34, 197, 94, 0.8)",    // Green for GB-4
          "rgba(245, 158, 11, 0.8)",   // Amber for Custom
        ],
        borderRadius: 6,
        barThickness: 60,
        borderColor: [
          "rgba(220, 38, 38, 1)",
          "rgba(37, 99, 235, 1)",
          "rgba(34, 197, 94, 1)",
          "rgba(245, 158, 11, 1)",
        ],
        borderWidth: 1,
        hoverBackgroundColor: [
          "rgba(220, 38, 38, 1)",
          "rgba(37, 99, 235, 1)",
          "rgba(34, 197, 94, 1)",
          "rgba(245, 158, 11, 1)",
        ],
      },
    ],
  };

  const handleYearChange = (e: any) => {
    setSelectedYear(e.target.value);
  };

  if (isLoading && !selectedYear) {
    return (
      <ChartCard title="Product Page Visits">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading product data...</div>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Product Page Visits">
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {selectedYear && (
              <div className="text-sm text-gray-600 px-3 py-2">
                <span className="font-medium">Total {selectedYear} Visits:</span>{" "}
                {productData.reduce((sum: any, count: any) => sum + count, 0)}
              </div>
            )}
            <div className="relative w-full max-w-[90px]">
              <select
                id="year-select"
                value={selectedYear}
                onChange={handleYearChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 appearance-none pr-10"
                disabled={years.length === 0}
              >
                {years.length === 0 ? (
                  <option value="">No data available</option>
                ) : (
                  years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))
                )}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
        </div>
      </div>

      <div className="h-64">
        <Bar
          data={productServiceVisitsData}
          options={productChartOptions}
        />
      </div>
    </ChartCard>
  );
}